import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ImagingViewerUIState, SliceItem } from '@packages/types/imagingViewer';
import type {
    ImagingStudy,
    ImagingSlice,
    PHIBoundingBox,
    DeIDResult,
    DeIDPipelineResult,
    DeIDProfile,
    PHITagSet,
    MetadataTagDecision,
    PHIDecision,
} from '@client/features/DeIdentification/types';
import type { TextFile } from '@client/features/DeIdentification/data/mockTextFiles';

// Extended state that includes text files and tab state
export interface ExtendedImagingViewerState extends ImagingViewerUIState {
    textFiles: TextFile[];
    selectedTextFileId?: string;
    activeTab: 'imaging' | 'text';
    hideImagingTab: boolean;
    strictDeidMetadata: boolean;
    isDeidLoading: boolean;
    showExport: boolean;
}

// Re-export for convenience
export type ImagingViewerState = ExtendedImagingViewerState;

const initialState: ImagingViewerState = {
    imagingStudies: [],
    selectedStudyId: null,
    isLoadingStudies: false,
    isLoadingDeidStudies: false,
    isLoadingSeries: false,
    isLoadingInstances: false,
    isLoadingDeidSeries: false,
    isLoadingDeidInstances: false,
    slices: [],
    anonymizedSlices: [],
    filteredOriginalSlices: [],
    filteredDeidSlices: [],
    currentSliceIndex: 0,
    allBoundingBoxes: [],
    maskedBoundingBoxes: [],
    originalMetadata: undefined,
    anonymizedMetadata: undefined,
    isLoadingMetadata: false,
    phiTagSet: undefined,
    selectedProfile: undefined,
    isDualDatastoreComparison: false,
    originalItems: [],
    deidItems: [],
    selectedOriginalSliceId: undefined,
    selectedDeidSliceId: undefined,
    deIDResultId: undefined,
    deIDResultStatus: undefined,
    deIDResult: undefined,
    pipelineResult: undefined,
    firstSeriesId: undefined,
    metadataDecisions: {},
    phiDecisions: {},
    isWaitingForDicomOnDeid018: false,
    selectedBoundingBox: null,
    decisionPanelVisible: false,
    // Text files and tab state
    textFiles: [],
    selectedTextFileId: undefined,
    activeTab: 'imaging' as 'imaging' | 'text',
    hideImagingTab: false,
    strictDeidMetadata: false,
    isDeidLoading: false,
    showExport: false,
};

const imagingViewerSlice = createSlice({
    name: 'imagingViewer',
    initialState,
    reducers: {
        // Studies
        setImagingStudies: (state, action: PayloadAction<ImagingStudy[]>) => {
            state.imagingStudies = action.payload;
        },
        setSelectedStudyId: (state, action: PayloadAction<string | null>) => {
            state.selectedStudyId = action.payload;
            // Reset slice index when study changes
            state.currentSliceIndex = 0;
        },
        setLoadingStudies: (
            state,
            action: PayloadAction<{ isLoadingStudies: boolean; isLoadingDeidStudies: boolean }>
        ) => {
            state.isLoadingStudies = action.payload.isLoadingStudies;
            state.isLoadingDeidStudies = action.payload.isLoadingDeidStudies;
        },

        // Series and Instances Loading
        setLoadingSeries: (state, action: PayloadAction<boolean>) => {
            state.isLoadingSeries = action.payload;
        },
        setLoadingInstances: (state, action: PayloadAction<boolean>) => {
            state.isLoadingInstances = action.payload;
        },
        setLoadingDeidSeries: (state, action: PayloadAction<boolean>) => {
            state.isLoadingDeidSeries = action.payload;
        },
        setLoadingDeidInstances: (state, action: PayloadAction<boolean>) => {
            state.isLoadingDeidInstances = action.payload;
        },

        // Slices
        setSlices: (state, action: PayloadAction<ImagingSlice[]>) => {
            state.slices = action.payload;
            // Reset slice index if out of bounds
            if (state.currentSliceIndex >= action.payload.length) {
                state.currentSliceIndex = 0;
            }
        },
        setAnonymizedSlices: (state, action: PayloadAction<ImagingSlice[]>) => {
            state.anonymizedSlices = action.payload;
        },
        setFilteredOriginalSlices: (state, action: PayloadAction<ImagingSlice[]>) => {
            state.filteredOriginalSlices = action.payload;
            // Reset slice index if out of bounds
            if (state.currentSliceIndex >= action.payload.length) {
                state.currentSliceIndex = 0;
            }
        },
        setFilteredDeidSlices: (state, action: PayloadAction<ImagingSlice[]>) => {
            state.filteredDeidSlices = action.payload;
        },
        setCurrentSliceIndex: (state, action: PayloadAction<number>) => {
            const maxIndex = Math.max(state.filteredOriginalSlices.length, state.filteredDeidSlices.length) - 1;
            state.currentSliceIndex = Math.max(0, Math.min(action.payload, maxIndex));
        },

        // Bounding Boxes
        setAllBoundingBoxes: (state, action: PayloadAction<PHIBoundingBox[]>) => {
            state.allBoundingBoxes = action.payload;
        },
        setMaskedBoundingBoxes: (state, action: PayloadAction<PHIBoundingBox[]>) => {
            state.maskedBoundingBoxes = action.payload;
        },

        // Metadata
        setOriginalMetadata: (state, action: PayloadAction<Record<string, any> | undefined>) => {
            state.originalMetadata = action.payload;
        },
        setAnonymizedMetadata: (state, action: PayloadAction<Record<string, any> | undefined>) => {
            state.anonymizedMetadata = action.payload;
        },
        setIsLoadingMetadata: (state, action: PayloadAction<boolean>) => {
            state.isLoadingMetadata = action.payload;
        },

        // Configuration
        setPhiTagSet: (state, action: PayloadAction<PHITagSet | undefined>) => {
            state.phiTagSet = action.payload;
        },
        setSelectedProfile: (state, action: PayloadAction<DeIDProfile | undefined>) => {
            state.selectedProfile = action.payload;
        },
        setIsDualDatastoreComparison: (state, action: PayloadAction<boolean>) => {
            state.isDualDatastoreComparison = action.payload;
        },

        // Slice Items
        setOriginalItems: (state, action: PayloadAction<SliceItem[]>) => {
            state.originalItems = action.payload;
        },
        setDeidItems: (state, action: PayloadAction<SliceItem[]>) => {
            state.deidItems = action.payload;
        },
        setSelectedOriginalSliceId: (state, action: PayloadAction<string | undefined>) => {
            state.selectedOriginalSliceId = action.payload;
        },
        setSelectedDeidSliceId: (state, action: PayloadAction<string | undefined>) => {
            state.selectedDeidSliceId = action.payload;
        },

        // DeID Result
        setDeIDResultId: (state, action: PayloadAction<string | undefined>) => {
            state.deIDResultId = action.payload;
        },
        setDeIDResultStatus: (state, action: PayloadAction<string | undefined>) => {
            state.deIDResultStatus = action.payload;
        },
        setDeIDResult: (state, action: PayloadAction<DeIDResult | undefined>) => {
            state.deIDResult = action.payload;
        },
        setPipelineResult: (state, action: PayloadAction<DeIDPipelineResult | undefined>) => {
            state.pipelineResult = action.payload;
        },
        setFirstSeriesId: (state, action: PayloadAction<string | undefined>) => {
            state.firstSeriesId = action.payload;
        },

        // Decisions
        setMetadataDecisions: (
            state,
            action: PayloadAction<Record<string, MetadataTagDecision> | Map<string, MetadataTagDecision>>
        ) => {
            // Handle both Map and Record for backward compatibility
            if (action.payload instanceof Map) {
                state.metadataDecisions = Object.fromEntries(action.payload);
            } else {
                state.metadataDecisions = action.payload;
            }
        },
        setPhiDecisions: (state, action: PayloadAction<Record<string, PHIDecision> | Map<string, PHIDecision>>) => {
            // Handle both Map and Record for backward compatibility
            if (action.payload instanceof Map) {
                state.phiDecisions = Object.fromEntries(action.payload);
            } else {
                state.phiDecisions = action.payload;
            }
        },

        // UI State
        setIsWaitingForDicomOnDeid018: (state, action: PayloadAction<boolean | undefined>) => {
            state.isWaitingForDicomOnDeid018 = action.payload;
        },

        // PHI Decision Panel
        setSelectedBoundingBox: (state, action: PayloadAction<PHIBoundingBox | null>) => {
            state.selectedBoundingBox = action.payload;
        },
        setDecisionPanelVisible: (state, action: PayloadAction<boolean>) => {
            state.decisionPanelVisible = action.payload;
        },

        // Text files and tab state
        setTextFiles: (state, action: PayloadAction<TextFile[]>) => {
            state.textFiles = action.payload;
        },
        setSelectedTextFileId: (state, action: PayloadAction<string | undefined>) => {
            state.selectedTextFileId = action.payload;
        },
        setActiveTab: (state, action: PayloadAction<'imaging' | 'text'>) => {
            state.activeTab = action.payload;
        },
        setHideImagingTab: (state, action: PayloadAction<boolean>) => {
            state.hideImagingTab = action.payload;
        },
        setStrictDeidMetadata: (state, action: PayloadAction<boolean>) => {
            state.strictDeidMetadata = action.payload;
        },
        setIsDeidLoading: (state, action: PayloadAction<boolean>) => {
            state.isDeidLoading = action.payload;
        },
        setShowExport: (state, action: PayloadAction<boolean>) => {
            state.showExport = action.payload;
        },

        // Reset all state
        resetViewerState: () => initialState,
    },
});

export const {
    setImagingStudies,
    setSelectedStudyId,
    setLoadingStudies,
    setLoadingSeries,
    setLoadingInstances,
    setLoadingDeidSeries,
    setLoadingDeidInstances,
    setSlices,
    setAnonymizedSlices,
    setFilteredOriginalSlices,
    setFilteredDeidSlices,
    setCurrentSliceIndex,
    setAllBoundingBoxes,
    setMaskedBoundingBoxes,
    setOriginalMetadata,
    setAnonymizedMetadata,
    setIsLoadingMetadata,
    setPhiTagSet,
    setSelectedProfile,
    setIsDualDatastoreComparison,
    setOriginalItems,
    setDeidItems,
    setSelectedOriginalSliceId,
    setSelectedDeidSliceId,
    setDeIDResultId,
    setDeIDResultStatus,
    setDeIDResult,
    setPipelineResult,
    setFirstSeriesId,
    setMetadataDecisions,
    setPhiDecisions,
    setIsWaitingForDicomOnDeid018,
    setSelectedBoundingBox,
    setDecisionPanelVisible,
    setTextFiles,
    setSelectedTextFileId,
    setActiveTab,
    setHideImagingTab,
    setStrictDeidMetadata,
    setIsDeidLoading,
    setShowExport,
    resetViewerState,
} = imagingViewerSlice.actions;

export default imagingViewerSlice.reducer;
